import { useEffect, useMemo, useState, type ReactNode } from "react"
import { format } from "date-fns"
import { useTranslation } from "react-i18next"
import {
  Bell,
  ExternalLink,
  FileText,
  Loader2,
  Mail,
  Webhook,
} from "lucide-react"
import { Link } from "react-router"

import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"
import { useUiTranslation } from "@f2e/ui/i18n"
import {
  EmptyDataPanel,
  LoadingPage,
  OnboardingEmptyState,
} from "@/components/System"
import { FilterSelect, PageNav } from "@/components"
import { Agent, Identity, Permission, Workspace } from "@/types"
import { useStore } from "@/store"
import {
  FetchStatus,
  FormRequestSubmissionResponse,
  FormRequestSchemaOptionResponse,
  useGetFormRequestsApi,
  useGetFormRequestSchemasApi,
  useGetAgentsApi,
  useGetPublishedAgentsApi,
  useGetNotificationDestinationsApi,
  useGetNotificationEventsApi,
  agentResponseToAgent,
  notificationDestinationResponseToDestination,
} from "@/hooks/api"
import type {
  NotificationDestination,
  NotificationRuleSchema,
} from "@/hooks/api/notifications"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"
import { downloadAttachmentResource } from "@/lib/attachmentResource"
import { hasPermission } from "@/lib/utils"
import { useSysConfig } from "@/hooks/useSysConfig"

export interface FormRequestsTabProps {
  workspace: Workspace
}

interface FormRequestRow {
  id: string
  historyId: number
  schemaId?: string | null
  schemaTitle?: string | null
  submittedIdentity?: {
    kind: Identity["kind"]
    userId?: number | null
    userEmail?: string | null
    externalUserId?: string | null
  } | null
  submittedAt?: Date
  submission: Record<string, unknown>
}

interface FormNotificationSummary {
  rules: NotificationRuleSchema[]
  destinations: NotificationDestination[]
}

const FORM_REQUESTS_PAGE_LIMIT = 20
const FORM_REQUEST_SUBMITTED_EVENT = "form_request.submitted"

const formRuleScope = (conditions: Record<string, unknown> | null) => {
  const agentId =
    typeof conditions?.agent_id === "string" ? conditions.agent_id : ""
  const schemaId =
    typeof conditions?.schema_id === "string" ? conditions.schema_id : ""
  return agentId && schemaId ? { agentId, schemaId } : null
}

const notificationStatusBadgeClassName = (
  tone: "positive" | "negative" | "neutral"
) =>
  cn(
    "shrink-0 font-medium",
    tone === "positive" &&
      "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-300",
    tone === "negative" &&
      "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
  )

const FormRequestsTab = ({ workspace }: FormRequestsTabProps) => {
  const { t } = useTranslation()
  const { t: uiT } = useUiTranslation()
  const sysConfig = useSysConfig()
  const [downloadError, setDownloadError] = useState<string>()
  const userMe = useStore((state) => state.userMe)
  const organizationId =
    workspace.organizationId || userMe?.profile?.defaultOrganizationId || ""
  const canManageNotifications = hasPermission(
    workspace.myPermissions,
    Permission.WsUpdateResources
  )

  const [rows, setRows] = useState<FormRequestRow[]>([])
  const [page, setPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [totalRecords, setTotalRecords] = useState<number>(0)

  const [agentFilter, setAgentFilter] = useState<string>("all")
  const [schemaFilter, setSchemaFilter] = useState<string>("all")

  const [agents, setAgents] = useState<Agent[]>([])
  const [schemaOptions, setSchemaOptions] = useState<
    FormRequestSchemaOptionResponse[]
  >([])
  const [notificationSummary, setNotificationSummary] =
    useState<FormNotificationSummary | null>(null)
  const [isNotificationSummaryLoading, setIsNotificationSummaryLoading] =
    useState(false)
  const [notificationSummaryError, setNotificationSummaryError] =
    useState(false)

  // Load agents for the current workspace to support agent filter
  const {
    data: agentsResp,
    status: agentsStatus,
    fetchFn: fetchAgents,
  } = useGetAgentsApi()
  const { data: publishedAgentsResp, fetchFn: fetchPublishedAgents } =
    useGetPublishedAgentsApi()

  // Load schema options separately (not from filtered results)
  const { data: schemasResp, fetchFn: fetchSchemas } =
    useGetFormRequestSchemasApi()

  const { data, status, error, fetchFn } = useGetFormRequestsApi()
  const { fetchFn: fetchNotificationEvents } = useGetNotificationEventsApi(
    organizationId,
    workspace.id
  )
  const { fetchFn: fetchNotificationDestinations } =
    useGetNotificationDestinationsApi(organizationId, workspace.id)

  useEffect(() => {
    if (!organizationId) {
      setNotificationSummary(null)
      setIsNotificationSummaryLoading(false)
      return
    }

    let active = true
    setNotificationSummary(null)
    setNotificationSummaryError(false)
    setIsNotificationSummaryLoading(true)
    Promise.all([fetchNotificationEvents(), fetchNotificationDestinations()])
      .then(([eventsResponse, destinationsResponse]) => {
        if (!active) return
        const formEvent = eventsResponse?.data?.find(
          (event) => event.event_type === FORM_REQUEST_SUBMITTED_EVENT
        )
        setNotificationSummary({
          rules: formEvent?.rules || [],
          destinations: (destinationsResponse?.data || []).map(
            notificationDestinationResponseToDestination
          ),
        })
      })
      .catch((error) => {
        if (!active) return
        setNotificationSummaryError(true)
        console.error(
          "Failed to fetch form request notification summary",
          error
        )
      })
      .finally(() => {
        if (active) setIsNotificationSummaryLoading(false)
      })

    return () => {
      active = false
    }
  }, [
    fetchNotificationDestinations,
    fetchNotificationEvents,
    organizationId,
    workspace.id,
  ])

  useEffect(() => {
    if (!userMe?.profile?.defaultOrganizationId) return

    const params: Record<string, string> = {
      oid: userMe.profile.defaultOrganizationId,
      wid: workspace.id,
    }

    fetchAgents({
      searchParams: new URLSearchParams(params),
    }).catch((error) => {
      console.error("Failed to fetch agents for form requests filter", error)
    })
    fetchPublishedAgents({
      searchParams: new URLSearchParams(params),
    }).catch((error) => {
      console.error("Failed to fetch published forms for form requests", error)
    })
  }, [
    fetchAgents,
    fetchPublishedAgents,
    userMe?.profile?.defaultOrganizationId,
    workspace.id,
  ])

  useEffect(() => {
    if (!agentsResp?.data || agentsStatus !== FetchStatus.Success) return

    const mapped = agentsResp.data
      .map((resp) => agentResponseToAgent(resp))
      .filter((agent) => agent.workspaceId === workspace.id)

    setAgents(mapped)
  }, [agentsResp, agentsStatus, workspace.id])

  // Fetch schema options separately (independent of filtered results)
  useEffect(() => {
    const params = new URLSearchParams()
    params.set("workspace_id", workspace.id)
    if (agentFilter !== "all") {
      params.set("agent_id", agentFilter)
    }

    fetchSchemas({ searchParams: params }).catch((error) => {
      console.error("Failed to fetch form request schemas", error)
    })
  }, [fetchSchemas, workspace.id, agentFilter])

  // Update schema options state when fetched
  useEffect(() => {
    if (schemasResp?.data) {
      setSchemaOptions(schemasResp.data)
    }
  }, [schemasResp])

  useEffect(() => {
    const params = new URLSearchParams()
    params.set("workspace_id", workspace.id)
    params.set("limit", FORM_REQUESTS_PAGE_LIMIT.toString())
    params.set("offset", ((page - 1) * FORM_REQUESTS_PAGE_LIMIT).toString())
    if (agentFilter !== "all") {
      params.set("agent_id", agentFilter)
    }
    if (schemaFilter !== "all") {
      params.set("schema_id", schemaFilter)
    }

    fetchFn({ searchParams: params }).catch(() => {
      // errors are handled via status & error
    })
  }, [fetchFn, workspace.id, page, agentFilter, schemaFilter])

  useEffect(() => {
    // Handle empty or undefined data
    if (!data?.data) {
      setRows([])
      setTotalPages(1)
      setTotalRecords(0)
      return
    }

    // Map and deduplicate by ID to prevent duplicate keys
    const idMap = new Map<string, FormRequestRow>()
    data.data.forEach((item: FormRequestSubmissionResponse) => {
      // Skip if we've already seen this ID (deduplicate)
      if (idMap.has(item.id)) {
        return
      }

      const submittedAt = item.submitted_at
        ? new Date(item.submitted_at)
        : undefined

      idMap.set(item.id, {
        id: item.id,
        historyId: item.history_id,
        schemaId: item.schema_id,
        schemaTitle: item.schema_title,
        submittedIdentity: item.submitted_identity
          ? {
              kind: item.submitted_identity.kind,
              userId: item.submitted_identity.user_id,
              userEmail: item.submitted_identity.user_email,
              externalUserId: item.submitted_identity.external_user_id,
            }
          : null,
        submittedAt,
        submission: item.submission || {},
      })
    })

    setRows(Array.from(idMap.values()))
    setTotalPages(data.pagination?.total_pages || 1)
    setTotalRecords(data.pagination?.total_records || 0)
  }, [data])

  const dynamicFieldKeys = useMemo(() => {
    const keys = new Set<string>()
    rows.forEach((row) => {
      Object.keys(row.submission || {}).forEach((key) => {
        keys.add(key)
      })
    })
    return Array.from(keys)
  }, [rows])

  const agentOptions = useMemo(
    () => [
      {
        value: "all",
        label: t("workspace.formRequests.filters.allAgents"),
      },
      ...agents.map((agent) => ({
        value: agent.id,
        label: agent.name || agent.id,
        data: agent,
      })),
    ],
    [agents, t]
  )

  const publishedAgents = useMemo(
    () =>
      (publishedAgentsResp?.data || [])
        .map((agent) => agentResponseToAgent(agent))
        .filter((agent) => agent.workspaceId === workspace.id),
    [publishedAgentsResp, workspace.id]
  )

  const availableFormSchemaOptions = useMemo(() => {
    const mergedOptions = new Map(
      schemaOptions.map((schema) => [schema.schema_id, schema])
    )
    const scopedPublishedAgents =
      agentFilter === "all"
        ? publishedAgents
        : publishedAgents.filter((agent) => agent.id === agentFilter)

    scopedPublishedAgents.forEach((agent) => {
      agent.unifiedTools.forEach((tool) => {
        if (tool.type !== "request_form" || !tool.customFormSchema?.id) return
        mergedOptions.set(tool.customFormSchema.id, {
          schema_id: tool.customFormSchema.id,
          schema_title: tool.customFormSchema.title,
        })
      })
    })

    return Array.from(mergedOptions.values()).sort((left, right) =>
      (left.schema_title || left.schema_id).localeCompare(
        right.schema_title || right.schema_id
      )
    )
  }, [agentFilter, publishedAgents, schemaOptions])

  const schemaOptionsMemo = useMemo(() => {
    return [
      {
        value: "all",
        label: t("workspace.formRequests.filters.allSchemas"),
      },
      ...availableFormSchemaOptions.map((schema) => ({
        value: schema.schema_id,
        label:
          schema.schema_title ||
          schema.schema_id ||
          t("workspace.formRequests.filters.unknownSchema"),
      })),
    ]
  }, [availableFormSchemaOptions, t])

  const formNotificationDetails = useMemo(() => {
    if (!notificationSummary) return null

    const destinationMap = new Map(
      notificationSummary.destinations.map((destination) => [
        destination.id,
        destination,
      ])
    )
    const destinationsForIds = (ids: string[]) =>
      Array.from(new Set(ids)).flatMap((id) => {
        const destination = destinationMap.get(id)
        return destination ? [destination] : []
      })
    const workspaceRule =
      notificationSummary.rules.find((rule) => rule.conditions === null) || null
    const individualRules = notificationSummary.rules.filter((rule) =>
      Boolean(formRuleScope(rule.conditions))
    )
    const workspaceDestinationIds =
      workspaceRule?.is_enabled === true
        ? workspaceRule.effective_destination_ids
        : []
    const selectedAgent =
      agentFilter === "all"
        ? null
        : agents.find((agent) => agent.id === agentFilter) || null
    const selectedSchemaAgentIds = new Set(
      schemaFilter === "all"
        ? []
        : [
            ...publishedAgents.flatMap((agent) =>
              agent.unifiedTools.some(
                (tool) =>
                  tool.type === "request_form" &&
                  tool.customFormSchema?.id === schemaFilter
              )
                ? [agent.id]
                : []
            ),
            ...individualRules.flatMap((rule) => {
              const scope = formRuleScope(rule.conditions)
              return scope?.schemaId === schemaFilter ? [scope.agentId] : []
            }),
          ]
    )
    const requiresAgentSelection =
      schemaFilter !== "all" &&
      agentFilter === "all" &&
      selectedSchemaAgentIds.size > 1
    const selectedRuleAgentId =
      agentFilter !== "all"
        ? agentFilter
        : selectedSchemaAgentIds.size === 1
          ? Array.from(selectedSchemaAgentIds)[0]
          : null
    const selectedRule =
      schemaFilter === "all" || !selectedRuleAgentId
        ? null
        : individualRules.find((rule) => {
            const scope = formRuleScope(rule.conditions)
            return (
              scope?.agentId === selectedRuleAgentId &&
              scope.schemaId === schemaFilter
            )
          }) || null
    const scopedIndividualRules =
      agentFilter === "all"
        ? individualRules
        : individualRules.filter(
            (rule) => formRuleScope(rule.conditions)?.agentId === agentFilter
          )
    return {
      workspaceRuleStatus: workspaceRule
        ? workspaceRule.is_enabled
          ? "enabled"
          : "disabled"
        : "notConfigured",
      workspaceDestinations: destinationsForIds(workspaceDestinationIds),
      individualRuleCount: scopedIndividualRules.length,
      selectedAgentName: selectedAgent?.name || null,
      selectedFormName:
        schemaFilter === "all"
          ? null
          : availableFormSchemaOptions.find(
              (schema) => schema.schema_id === schemaFilter
            )?.schema_title || schemaFilter,
      requiresAgentSelection,
      selectedBehavior:
        schemaFilter === "all" || requiresAgentSelection
          ? null
          : selectedRule
            ? selectedRule.is_enabled
              ? "custom"
              : "notificationsOff"
            : "workspaceDefault",
      selectedDestinations: destinationsForIds(
        requiresAgentSelection
          ? []
          : selectedRule
            ? selectedRule.is_enabled
              ? selectedRule.effective_destination_ids
              : []
            : workspaceDestinationIds
      ),
    }
  }, [
    agentFilter,
    agents,
    availableFormSchemaOptions,
    notificationSummary,
    publishedAgents,
    schemaFilter,
  ])

  const renderValue = (value: unknown): ReactNode => {
    if (Array.isArray(value)) {
      return value.join(", ")
    }
    if (value === null || value === undefined || value === "") {
      return "—"
    }
    if (
      typeof value === "object" &&
      "attachmentId" in value &&
      "name" in value &&
      typeof value.name === "string" &&
      "url" in value &&
      typeof value.url === "string"
    ) {
      const file = { name: value.name, url: value.url }
      return (
        <span>
          <Button
            variant="link"
            className="h-auto p-0 text-left whitespace-normal break-all"
            onClick={async () => {
              setDownloadError(undefined)
              if (
                !(await downloadAttachmentResource(
                  file,
                  sysConfig.serverRoot || window.location.origin
                ))
              ) {
                setDownloadError(file.url)
              }
            }}
          >
            <FileText className="mr-1 h-4 w-4 shrink-0" aria-hidden="true" />
            {file.name}
          </Button>
          {downloadError === file.url && (
            <span role="alert" className="block text-xs text-destructive">
              {uiT("form.file.downloadFailed")}
            </span>
          )}
        </span>
      )
    }
    return String(value)
  }

  const renderNotificationDestinations = (
    destinations: NotificationDestination[],
    emptyMessage?: string
  ) =>
    destinations.length > 0 ? (
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        {destinations.map((destination) => {
          const DestinationIcon =
            destination.destinationType === "email" ? Mail : Webhook
          return (
            <Badge
              key={destination.id}
              variant="secondary"
              className="max-w-full font-normal text-muted-foreground"
              title={destination.name}
            >
              <DestinationIcon />
              <span className="min-w-0 truncate">{destination.name}</span>
            </Badge>
          )
        })}
      </div>
    ) : emptyMessage ? (
      <span className="text-xs text-amber-700 dark:text-amber-300">
        {emptyMessage}
      </span>
    ) : null

  const notificationSettingsButton = organizationId ? (
    <Button
      variant="ghost"
      size="sm"
      className="shrink-0 text-muted-foreground"
      asChild
    >
      <Link
        to={`/workspaces/${workspace.id}?tab=notifications&event=${FORM_REQUEST_SUBMITTED_EVENT}`}
      >
        {t(
          canManageNotifications
            ? "workspace.notifications.manageWorkspace"
            : "workspace.notifications.viewWorkspace"
        )}
        <ExternalLink className="ml-2 h-3.5 w-3.5" />
      </Link>
    </Button>
  ) : null

  const formRequestDescription = (
    <CardDescription>{t("workspace.formRequests.description")}</CardDescription>
  )

  const notificationSummarySection = (
    <section
      className="border-t pt-3"
      aria-labelledby="form-notification-summary-title"
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex min-w-0 items-center gap-2">
          <Bell className="h-4 w-4 shrink-0 text-muted-foreground" />
          <h3
            id="form-notification-summary-title"
            className="shrink-0 text-sm font-medium"
          >
            {t("workspace.formRequests.notifications.title")}
          </h3>
          {formNotificationDetails?.selectedFormName ? (
            <span className="min-w-0 truncate text-xs text-muted-foreground">
              {formNotificationDetails.selectedFormName}
            </span>
          ) : null}
        </div>

        {isNotificationSummaryLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("common.processing")}
          </div>
        ) : notificationSummaryError ? (
          <span className="text-xs text-amber-700 dark:text-amber-300">
            {t("workspace.formRequests.notifications.loadFailed")}
          </span>
        ) : formNotificationDetails?.selectedFormName &&
          formNotificationDetails.requiresAgentSelection ? (
          <span className="text-xs text-muted-foreground">
            {t(
              "workspace.formRequests.notifications.selectAgentForFormSummary"
            )}
          </span>
        ) : formNotificationDetails?.selectedFormName &&
          formNotificationDetails.selectedBehavior ? (
          <>
            <Badge
              variant="outline"
              className={notificationStatusBadgeClassName(
                formNotificationDetails.selectedBehavior === "custom"
                  ? "positive"
                  : formNotificationDetails.selectedBehavior ===
                      "notificationsOff"
                    ? "negative"
                    : "neutral"
              )}
            >
              {t(
                `workspace.formRequests.notifications.behaviors.${formNotificationDetails.selectedBehavior}`
              )}
            </Badge>
            {renderNotificationDestinations(
              formNotificationDetails.selectedDestinations,
              formNotificationDetails.selectedBehavior === "notificationsOff"
                ? undefined
                : formNotificationDetails.selectedBehavior === "custom"
                  ? t(
                      "workspace.formRequests.notifications.customDestinationsUnavailable"
                    )
                  : t(
                      `workspace.formRequests.notifications.workspaceDestinationEmpty.${formNotificationDetails.workspaceRuleStatus}`
                    )
            )}
          </>
        ) : formNotificationDetails ? (
          <>
            <Badge
              variant="outline"
              className={notificationStatusBadgeClassName(
                formNotificationDetails.workspaceRuleStatus === "enabled"
                  ? "positive"
                  : formNotificationDetails.workspaceRuleStatus === "disabled"
                    ? "negative"
                    : "neutral"
              )}
            >
              {t(
                `workspace.formRequests.notifications.workspaceRuleStatuses.${formNotificationDetails.workspaceRuleStatus}`
              )}
            </Badge>
            {renderNotificationDestinations(
              formNotificationDetails.workspaceDestinations,
              formNotificationDetails.workspaceRuleStatus === "enabled"
                ? t(
                    "workspace.formRequests.notifications.workspaceDestinationEmpty.enabled"
                  )
                : undefined
            )}
            <span className="text-xs text-muted-foreground">
              {formNotificationDetails.selectedAgentName
                ? formNotificationDetails.individualRuleCount > 0
                  ? t(
                      "workspace.formRequests.notifications.individualSettingsForAgent",
                      {
                        agent: formNotificationDetails.selectedAgentName,
                        count: formNotificationDetails.individualRuleCount,
                      }
                    )
                  : t(
                      "workspace.formRequests.notifications.noIndividualSettingsForAgent",
                      {
                        agent: formNotificationDetails.selectedAgentName,
                      }
                    )
                : formNotificationDetails.individualRuleCount > 0
                  ? t(
                      "workspace.formRequests.notifications.individualSettings",
                      {
                        count: formNotificationDetails.individualRuleCount,
                      }
                    )
                  : t(
                      "workspace.formRequests.notifications.noIndividualSettings"
                    )}
            </span>
          </>
        ) : null}
        <div className="ml-auto">{notificationSettingsButton}</div>
      </div>
    </section>
  )

  if (status === FetchStatus.Loading) {
    return <LoadingPage message={t("workspace.agent.message.processing")} />
  }

  if (error) {
    return (
      <EmptyDataPanel
        message={getUserFacingApiErrorMessage(
          error,
          t,
          t("workspace.detail.errors.loadFailed")
        )}
      />
    )
  }

  return (
    <Card className="border border-slate-200 dark:border-slate-800">
      <CardHeader className="space-y-4">
        <div>
          <CardTitle>{t("workspace.formRequests.title")}</CardTitle>
          {formRequestDescription}
        </div>
        {notificationSummarySection}
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <FilterSelect
            label={
              agentOptions.find((opt) => opt.value === agentFilter)?.label ??
              t("workspace.formRequests.filters.allAgents")
            }
            options={agentOptions}
            onSelect={(value) => {
              setAgentFilter(value)
              setSchemaFilter("all")
              setSchemaOptions([])
              setPage(1)
            }}
          />
          <FilterSelect
            label={
              schemaOptionsMemo.find((opt) => opt.value === schemaFilter)
                ?.label ?? t("workspace.formRequests.filters.allSchemas")
            }
            options={schemaOptionsMemo}
            onSelect={(value) => {
              setSchemaFilter(value)
              setPage(1)
            }}
          />
        </div>

        {rows.length === 0 ? (
          <OnboardingEmptyState
            icon={FileText}
            title={t("workspace.formRequests.onboarding.title")}
            description={t("workspace.formRequests.onboarding.description")}
          />
        ) : (
          <>
            {/* Total Records */}
            {totalRecords > 0 && (
              <div className="mb-4 text-sm text-slate-600 dark:text-slate-400">
                {t("workspace.formRequests.totalRecords", {
                  count: totalRecords,
                })}
              </div>
            )}

            <div className="w-full overflow-x-auto">
              <Table>
                <TableHeader>
                  {/* First row: Fixed columns + Group header for dynamic fields */}
                  <TableRow>
                    <TableHead
                      rowSpan={dynamicFieldKeys.length > 0 ? 2 : 1}
                      className="border-r border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50"
                    >
                      {t("workspace.formRequests.table.formTitle")}
                    </TableHead>
                    <TableHead
                      rowSpan={dynamicFieldKeys.length > 0 ? 2 : 1}
                      className="border-r border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50"
                    >
                      {t("workspace.formRequests.table.externalUserId")}
                    </TableHead>
                    <TableHead
                      rowSpan={dynamicFieldKeys.length > 0 ? 2 : 1}
                      className="border-r border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50"
                    >
                      {t("workspace.formRequests.table.submittedAt")}
                    </TableHead>
                    {dynamicFieldKeys.length > 0 && (
                      <TableHead
                        colSpan={dynamicFieldKeys.length}
                        className="text-center font-semibold bg-slate-100 dark:bg-slate-800"
                      >
                        {t("workspace.formRequests.table.formFieldsGroup")}
                      </TableHead>
                    )}
                  </TableRow>
                  {/* Second row: Individual field names for dynamic columns */}
                  {dynamicFieldKeys.length > 0 && (
                    <TableRow>
                      {dynamicFieldKeys.map((key) => (
                        <TableHead
                          key={key}
                          className="bg-slate-100 dark:bg-slate-800"
                        >
                          {key}
                        </TableHead>
                      ))}
                    </TableRow>
                  )}
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="border-r border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/30">
                        {row.schemaTitle || row.schemaId || "—"}
                      </TableCell>
                      <TableCell className="border-r border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/30">
                        {row.submittedIdentity?.externalUserId ||
                          row.submittedIdentity?.userEmail ||
                          (row.submittedIdentity?.userId
                            ? `User #${row.submittedIdentity.userId}`
                            : null) ||
                          t("workspace.formRequests.table.unknownUser", {
                            defaultValue: "Unknown",
                          })}
                      </TableCell>
                      <TableCell className="border-r border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/30">
                        {row.submittedAt
                          ? format(row.submittedAt, "yyyy-MM-dd HH:mm")
                          : "—"}
                      </TableCell>
                      {dynamicFieldKeys.map((key) => (
                        <TableCell key={`${row.id}-${key}`}>
                          {renderValue(row.submission[key])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {totalPages > 1 && (
              <div className="mt-4">
                <PageNav
                  className="mb-2"
                  page={page}
                  totalPages={totalPages}
                  onPageChange={(nextPage) => {
                    setPage(nextPage)
                  }}
                />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default FormRequestsTab
